import React, { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { quotaCleanerSafeTip } from 'reactApp/appHelpers/featuresHelpers/features/quotaCleanerSafe';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { type CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getCurrentGroupId } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Move2SafeTooltip } from 'reactApp/ui/Toolbar/Move2SafeButton/Move2SafeTooltip';

import { sendDeleteButtonDwh } from './DeleteButton.helpers';

interface DeleteButtonProps {
    items: CloudItem[];
    totalSize?: number;
    totalLength?: number;
    hook: ({ items, totalSize, totalLength }) => {
        callback: () => void;
        title?: string;
        isMove2SafeEnabled?: boolean;
        deleteSize: {
            size: number;
            sizeString: string;
        };
    };
}

/**
 * Кнопка тулбара - Удаление чего-либо
 * @param hook - хук, который вернет коллбек (действие удаления), удаляемый размер
 * @param items - массив удаляемых элементов
 */
export const DeleteButton: FC<DeleteButtonProps> = ({ hook, items, totalSize, totalLength }) => {
    const { callback: deleteCallback, deleteSize, title, isMove2SafeEnabled } = hook({ items, totalSize, totalLength });
    const isMoveToSafeButton = !!title;
    const { size, sizeString } = deleteSize;
    const ref = useRef<HTMLDivElement>(null);
    const storage = useSelector(getCurrentStorage);
    const isQuotaCleaner = storage === EStorageType.quotaCleaner;
    const groupId = useSelector(getCurrentGroupId);

    const enableSafeTip = isMove2SafeEnabled && quotaCleanerSafeTip && isQuotaCleaner && !!size;
    const radarName = isMoveToSafeButton ? 'items_to_safe' : 'items_to_remove';

    const sizeText = useMemo(() => {
        if (!size) {
            return '';
        }

        return `\xA0${sizeString}`;
    }, [size, sizeString]);

    useEffect(() => {
        if (isQuotaCleaner && groupId) {
            sendDeleteButtonDwh({ action: 'show', name: radarName, groupId, source: 'cleaner' });
        }
    }, [isQuotaCleaner, groupId, radarName]);

    const handleClick = useCallback(() => {
        deleteCallback();

        if (isQuotaCleaner) {
            sendDeleteButtonDwh({ action: 'click', name: radarName, groupId, source: 'cleaner' });
        }
    }, [deleteCallback, isQuotaCleaner, groupId, radarName]);

    const button = (
        <Button
            theme="octavius"
            primary
            sizeMode={ButtonSizeMode.small}
            disabled={!size}
            onClick={handleClick}
            qaId={isMoveToSafeButton ? 'safe-btn' : 'delete-btn'}
        >
            {title || 'Удалить'}
            {!isMoveToSafeButton && sizeText}
        </Button>
    );

    if (enableSafeTip) {
        return (
            <div ref={ref}>
                {button}
                <Move2SafeTooltip targetRef={ref} />
            </div>
        );
    }

    return button;
};
